import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { apiCall } from '../services/apiUtil';
import { Mail, CheckCircle, AlertCircle, Shield } from 'lucide-react';

const UnsubscribePage = () => {
  const { theme } = useTheme();
  const { user, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null); // 'success' | 'error' | 'link-sent'
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Logged-out: request unsubscribe link via email
  const handleRequestLink = async (e) => {
    e.preventDefault();
    if (!email || loading) return;

    setLoading(true);
    setStatus(null);
    try {
      const res = await apiCall('/newsletter/unsubscribe', {
        method: 'POST',
        body: JSON.stringify({ email })
      });
      if (res.message && res.message.includes('already unsubscribed')) {
        setStatus('already-unsubscribed');
      } else {
        setStatus('link-sent');
      }
      setEmail('');
    } catch {
      setStatus('link-sent'); // Always show same message for security
    } finally {
      setLoading(false);
    }
  };

  // Logged-in: direct unsubscribe using authenticated session
  const handleDirectUnsubscribe = async () => {
    if (loading) return;

    setLoading(true);
    setStatus(null);
    try {
      const res = await apiCall('/newsletter/unsubscribe/direct', {
        method: 'POST'
      });
      if (res.already_unsubscribed) {
        setStatus('already-unsubscribed');
      } else {
        setStatus('success');
      }
    } catch (err) {
      setStatus('error');
      setMessage(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Success State ─────────────────────────────────────────────────────────
  if (status === 'success') {
    return (
      <div className={`min-h-screen ${theme.background} flex items-start justify-center pt-8 sm:pt-16 px-4`}>
        <div className={`${theme.card} rounded-xl border ${theme.border} p-6 sm:p-8 max-w-md w-full`}>
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-7 h-7 text-green-500" />
            </div>
            <h1 className={`text-xl sm:text-2xl font-bold ${theme.text} mb-2`}>You're all set</h1>
            <p className={`${theme.textMuted} text-sm sm:text-base mb-6`}>
              We've removed your email from the Kynaix newsletter. You won't receive any more marketing updates from us.
            </p>
            <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-left`}>
              <p className={`text-xs sm:text-sm ${theme.textMuted}`}>
                Changed your mind? You can re-subscribe anytime at{' '}
                <a href="/" className="text-blue-500 hover:text-blue-600 font-medium">kynaixsports.com</a>.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Link Sent State (logged-out) ──────────────────────────────────────────
  if (status === 'link-sent') {
    return (
      <div className={`min-h-screen ${theme.background} flex items-start justify-center pt-8 sm:pt-16 px-4`}>
        <div className={`${theme.card} rounded-xl border ${theme.border} p-6 sm:p-8 max-w-md w-full`}>
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-4">
              <Mail className="w-7 h-7 text-blue-500" />
            </div>
            <h1 className={`text-xl sm:text-2xl font-bold ${theme.text} mb-2`}>Check your inbox</h1>
            <p className={`${theme.textMuted} text-sm sm:text-base mb-4`}>
              If an account is associated with this email, a secure link to manage your subscription has been sent.
            </p>
            <p className={`${theme.textMuted} text-xs`}>
              The link expires in 10 minutes. Check your spam folder if you don't see it.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Already Unsubscribed State ─────────────────────────────────────────────
  if (status === 'already-unsubscribed') {
    return (
      <div className={`min-h-screen ${theme.background} flex items-start justify-center pt-8 sm:pt-16 px-4`}>
        <div className={`${theme.card} rounded-xl border ${theme.border} p-6 sm:p-8 max-w-md w-full`}>
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-7 h-7 text-green-500" />
            </div>
            <h1 className={`text-xl sm:text-2xl font-bold ${theme.text} mb-2`}>Already Unsubscribed</h1>
            <p className={`${theme.textMuted} text-sm sm:text-base mb-6`}>
              This email is already unsubscribed from the Kynaix newsletter. No further action is needed.
            </p>
            <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-left`}>
              <p className={`text-xs sm:text-sm ${theme.textMuted}`}>
                Want to re-subscribe? You can do so anytime at{' '}
                <a href="/" className="text-blue-500 hover:text-blue-600 font-medium">kynaixsports.com</a>.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Logged-In Confirmation Flow ───────────────────────────────────────────
  if (isAuthenticated && user?.email) {
    return (
      <div className={`min-h-screen ${theme.background} flex items-start justify-center pt-8 sm:pt-16 px-4`}>
        <div className={`${theme.card} rounded-xl border ${theme.border} p-6 sm:p-8 max-w-md w-full`}>
          <div className="flex items-center gap-3 mb-1">
            <div className={`w-10 h-10 rounded-lg ${theme.backgroundSecondary} flex items-center justify-center`}>
              <Shield className={`w-5 h-5 ${theme.textMuted}`} />
            </div>
            <h1 className={`text-xl sm:text-2xl font-bold ${theme.text}`}>Unsubscribe</h1>
          </div>
          <p className={`${theme.textMuted} text-sm sm:text-base mt-3 mb-2`}>
            You're signed in as:
          </p>
          <div className={`${theme.backgroundSecondary} rounded-lg px-4 py-3 mb-6`}>
            <p className={`font-medium ${theme.text} text-sm`}>{user.email}</p>
          </div>
          <p className={`${theme.textMuted} text-sm mb-6`}>
            Click below to unsubscribe this email from the Kynaix newsletter. You'll stop receiving marketing emails but will still get important account updates.
          </p>

          <button
            onClick={handleDirectUnsubscribe}
            disabled={loading}
            className={`w-full py-3 ${theme.secondary} border ${theme.border} ${theme.text} rounded-lg font-medium transition-colors hover:opacity-80 disabled:opacity-50 text-sm sm:text-base`}
          >
            {loading ? 'Processing...' : 'Confirm & Unsubscribe'}
          </button>

          {status === 'error' && (
            <div className="flex items-start gap-2 mt-4 p-3 rounded-lg bg-red-500/10">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <p className="text-sm text-red-500">{message}</p>
            </div>
          )}

          <div className={`border-t ${theme.border} mt-6 pt-4`}>
            <p className={`text-xs ${theme.textMuted} text-center`}>
              This only affects marketing emails. Transactional emails (like password resets) will continue as normal.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Logged-Out Form ───────────────────────────────────────────────────────
  return (
    <div className={`min-h-screen ${theme.background} flex items-start justify-center pt-8 sm:pt-16 px-4`}>
      <div className={`${theme.card} rounded-xl border ${theme.border} p-6 sm:p-8 max-w-md w-full`}>
        <div className="flex items-center gap-3 mb-1">
          <div className={`w-10 h-10 rounded-lg ${theme.backgroundSecondary} flex items-center justify-center`}>
            <Mail className={`w-5 h-5 ${theme.textMuted}`} />
          </div>
          <h1 className={`text-xl sm:text-2xl font-bold ${theme.text}`}>Email Preferences</h1>
        </div>
        <p className={`${theme.textMuted} text-sm sm:text-base mt-3 mb-6`}>
          Enter your email below and we'll send you a secure link to manage your newsletter subscription.
        </p>

        <form onSubmit={handleRequestLink} className="space-y-4">
          <div>
            <label className={`text-xs font-medium ${theme.textMuted} mb-1.5 block`}>Email address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className={`w-full px-4 py-3 ${theme.input} border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base`}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 ${theme.secondary} border ${theme.border} ${theme.text} rounded-lg font-medium transition-colors hover:opacity-80 disabled:opacity-50 text-sm sm:text-base`}
          >
            {loading ? 'Sending...' : 'Send Unsubscribe Link'}
          </button>
        </form>

        {status === 'error' && (
          <div className="flex items-start gap-2 mt-4 p-3 rounded-lg bg-red-500/10">
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
            <p className="text-sm text-red-500">{message}</p>
          </div>
        )}

        <div className={`border-t ${theme.border} mt-6 pt-4`}>
          <p className={`text-xs ${theme.textMuted} text-center`}>
            This only affects marketing emails. Transactional emails (like password resets) will continue as normal.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnsubscribePage;
